/**
 * Collision contract (runtime-handoff-v5, directive §16).
 *
 * stadium_collision.glb holds invisible collision geometry: meshes prefixed
 * `COLLIDER_`. They must NOT be rendered in production; a debug toggle draws them
 * as wireframes. Collision is derived ONLY from these simple colliders, never
 * from the detailed visual stadium mesh.
 */
export const COLLIDER_PREFIX = 'COLLIDER_';

export function isColliderName(name: string | undefined | null): boolean {
  return typeof name === 'string' && name.startsWith(COLLIDER_PREFIX);
}

/** A minimal node shape so this is testable without a three scene graph. */
export interface SceneNodeLike {
  name?: string;
  isMesh?: boolean;
  visible?: boolean;
  children?: SceneNodeLike[];
}

export interface ColliderPartition {
  colliders: SceneNodeLike[];
  /** Renderable meshes accidentally left in the collision file (should be empty). */
  strayVisible: SceneNodeLike[];
}

/**
 * Walk a loaded collision scene: collect COLLIDER_ meshes and flag any renderable
 * mesh that is NOT a collider (which would wrongly draw in production).
 */
export function partitionColliders(root: SceneNodeLike): ColliderPartition {
  const colliders: SceneNodeLike[] = [];
  const strayVisible: SceneNodeLike[] = [];
  const walk = (n: SceneNodeLike): void => {
    if (n.isMesh) {
      if (isColliderName(n.name)) colliders.push(n);
      else strayVisible.push(n);
    }
    for (const c of n.children ?? []) walk(c);
  };
  walk(root);
  return { colliders, strayVisible };
}

/** Hide every collider for production rendering (they stay in the physics world). */
export function hideCollidersForProduction(colliders: readonly SceneNodeLike[]): void {
  for (const c of colliders) c.visible = false;
}
